/**
 * Browser entry point for the imis-utilities library.
 *
 * This is the module bundled for browser/UMD consumption. It imports every jQuery/DOM/Telerik-dependent
 * utility and model, re-exports them under short aliases (e.g. `General`, `Rest`, `Payload`, `Url`) for
 * ES-module/bundler consumers, and augments the global `Window` interface with a `VAIMISUtilityV2`
 * namespace so that script-tag consumers referencing `window.VAIMISUtilityV2.*` get type checking.
 * Importing this module also side-effect imports the ambient Telerik type declarations and the
 * initialization utility, which wires up query-editor, form-builder, and panel-source buttons
 * as soon as this module loads.
 */
import { Helper } from "./helpers/helper";
import { ImisAuthentication as Authentication, ImisMessageType as MessageType, ImisPayloadFieldType as PayloadFieldType } from "./models/imis.models";
import { ContextUtility } from "./utilities/context.utility";
import { DataSourcesUtility } from "./utilities/dataSources.utility";
import { FilesUtility } from "./utilities/file.utility";
import * as FormsUtility from "./utilities/forms.utility";
import { GenTablesUtility } from "./utilities/genTables.utility";
import * as HtmlUtility from "./utilities/html.utility";
import { LoaderUtility } from "./utilities/loader.utility";
import { MessagesUtility } from "./utilities/messages.utility";
import { PanelsUtility } from "./utilities/panels.utility";
import { PayloadUtility } from "./utilities/payload.utility";
import { RestUtility } from "./utilities/rest.utility";
import { UrlUtility } from "./utilities/url.utility";
import { WebFormsUtility } from "./utilities/webForms.utility";
import "./models/declarations/telerik.web.ui";
import { ChangeLogUtility } from "./utilities/changeLog.utility";
import { iPartUtility } from "./utilities/iPart.utility";
import { PopupUtility } from "./utilities/popup.utility";
import { TelerikUtility } from "./utilities/telerik.utility";
import { DocumentsUtility } from "./utilities/documents.utility";
import { QueriesUtility } from "./utilities/queries.utility";
import * as InitializationUtility from "./utilities/initialization.utility";
import { LoginUtility } from "./utilities/login.utility";
import { PartyUtility } from "./utilities/party.utility";
import * as CartUtility from "./utilities/cart.utility";
import * as SystemUtility from "./utilities/system.utility";
import * as GroupsUtility from "./utilities/groups.utility";
import * as IntegrationsUtility from "./utilities/integrations.utility";

/**
 * Global namespace declaration for the UMD build. This block does not exist at runtime on its own —
 * it declares the shape of the `VAIMISUtilityV2` global that `./utilities/initialization.utility`-style
 * script-tag consumption exposes on `window`, so that TypeScript consumers referencing
 * `window.VAIMISUtilityV2.*` (or the ambient `VAIMISUtilityV2` global) get type checking and IntelliSense.
 */
declare global {
    namespace VAIMISUtilityV2 {
        /** Type-only mirror of the runtime `Models` namespace below, grouping model-related globals under `VAIMISUtilityV2.Models.*`. */
        namespace Models {
            const ImisAuthentication: typeof Authentication;
            const ImisMessageType: typeof MessageType;
            const ImisPayloadFieldType: typeof PayloadFieldType;
        }

        /** General-purpose string/number/date/password helper methods. */
        const General: typeof Helper;
        /** REST client for calling the iMIS API (authentication, requests, response handling). */
        const Rest: typeof RestUtility;
        /** Parses/maps raw iMIS REST payloads into typed objects. */
        const Payload: typeof PayloadUtility;
        /** URL building and query-string helpers. */
        const Url: typeof UrlUtility;
        /** Reads iMIS generic table (GenTable) definitions and values. */
        const GenTables: typeof GenTablesUtility;
        /** Looks up iMIS business object / panel data source definitions. */
        const DataSources: typeof DataSourcesUtility;
        /** DOM/Telerik grid helpers (dropdowns, option inputs, panels, grid relocation). */
        const Html: typeof HtmlUtility;
        /** Reads and opens iMIS panel definitions. */
        const Panels: typeof PanelsUtility;
        /** Adds/removes a loading indicator overlay. */
        const Loader: typeof LoaderUtility;
        /** Binary/base64/object-URL file conversion helpers. */
        const Files: typeof FilesUtility;
        /** Reads the iMIS client context embedded in the page. */
        const Context: typeof ContextUtility;
        /** Wraps the ASP.NET AJAX (Sys.WebForms) PageRequestManager begin/end request events. */
        const WebForms: typeof WebFormsUtility;
        /** Shows toast-style status/validation messages. */
        const Messages: typeof MessagesUtility;
        /** Locates form-builder generated fields and related form helpers (validators, webhooks, drag-drop, fields). */
        const Forms: typeof FormsUtility;
        /** Reads iMIS change log records for an object/property. */
        const ChangeLog: typeof ChangeLogUtility;
        /** Reads iPart content item settings. */
        const iPart: typeof iPartUtility;
        /** Opens/closes Telerik-based iMIS popup windows. */
        const Popup: typeof PopupUtility;
        /** Helpers for interacting with Telerik RadGrid/RadWindow controls. */
        const Telerik: typeof TelerikUtility;
        /** Reads iMIS document/business-object-definition summaries. */
        const Documents: typeof DocumentsUtility;
        /** Looks up and executes iMIS query definitions. */
        const Queries: typeof QueriesUtility;
        /** Retrieves SSO data from iMIS SSO pages. */
        const Login: typeof LoginUtility;
        /** Looks up party/user-level information (e.g. staff status). */
        const Party: typeof PartyUtility;
        /** Adds/removes products and gifts from the current user's cart. */
        const Cart: typeof CartUtility;
        /** iMIS out-of-the-box UI helpers (e.g. the image manager popup). */
        const System: typeof SystemUtility;
        /** Reads/updates iMIS group membership, including refreshing dynamic groups. */
        const Groups: typeof GroupsUtility;
        /** Executes and polls IDMS (iMIS data import) integration tasks. */
        const Integrations: typeof IntegrationsUtility;
    }

    interface Window {
        VAIMISUtilityV2: typeof VAIMISUtilityV2;
    }
}

/**
 * Groups the model-related re-exports (iMIS enums/constants) under a `Models.*` namespace,
 * mirroring the `VAIMISUtilityV2.Models` global declared above.
 */
namespace Models {
    export const ImisAuthentication = Authentication;
    export const ImisMessageType = MessageType;
    export const ImisPayloadFieldType = PayloadFieldType;
}

export {
    /** Side-effect module that wires up query-editor/form-builder/panel-source buttons on load; exported as `_internal` for internal use. */
    InitializationUtility as _internal,
    /** Model-related re-exports grouped under `Models.*` (see namespace above). */
    Models,
    /** General-purpose string/number/date/password helper methods. */
    Helper as General,
    /** REST client for calling the iMIS API (authentication, requests, response handling). */
    RestUtility as Rest,
    /** Parses/maps raw iMIS REST payloads into typed objects. */
    PayloadUtility as Payload,
    /** URL building and query-string helpers. */
    UrlUtility as Url,
    /** Reads iMIS generic table (GenTable) definitions and values. */
    GenTablesUtility as GenTables,
    /** Looks up iMIS business object / panel data source definitions. */
    DataSourcesUtility as DataSources,
    /** DOM/Telerik grid helpers (dropdowns, option inputs, panels, grid relocation). */
    HtmlUtility as Html,
    /** Reads and opens iMIS panel definitions. */
    PanelsUtility as Panels,
    /** Adds/removes a loading indicator overlay. */
    LoaderUtility as Loader,
    /** Binary/base64/object-URL file conversion helpers. */
    FilesUtility as Files,
    /** Reads the iMIS client context embedded in the page. */
    ContextUtility as Context,
    /** Wraps the ASP.NET AJAX (Sys.WebForms) PageRequestManager begin/end request events. */
    WebFormsUtility as WebForms,
    /** Shows toast-style status/validation messages. */
    MessagesUtility as Messages,
    /** Locates form-builder generated fields and related form helpers (validators, webhooks, drag-drop, fields). */
    FormsUtility as Forms,
    /** Reads iMIS change log records for an object/property. */
    ChangeLogUtility as ChangeLog,
    /** Reads iPart content item settings. */
    iPartUtility as iPart,
    /** Opens/closes Telerik-based iMIS popup windows. */
    PopupUtility as Popup,
    /** Helpers for interacting with Telerik RadGrid/RadWindow controls. */
    TelerikUtility as Telerik,
    /** Reads iMIS document/business-object-definition summaries. */
    DocumentsUtility as Documents,
    /** Looks up and executes iMIS query definitions. */
    QueriesUtility as Queries,
    /** Retrieves SSO data from iMIS SSO pages. */
    LoginUtility as Login,
    /** Looks up party/user-level information (e.g. staff status). */
    PartyUtility as Party,
    /** Adds/removes products and gifts from the current user's cart. */
    CartUtility as Cart,
    /** iMIS out-of-the-box UI helpers (e.g. the image manager popup). */
    SystemUtility as System,
    /** Reads/updates iMIS group membership, including refreshing dynamic groups. */
    GroupsUtility as Groups,
    /** Executes and polls IDMS (iMIS data import) integration tasks. */
    IntegrationsUtility as Integrations,
};
